import { ProductFlowEmployeeType } from "@/components/product-manager-components/ProductFlowEmployeeType";
import { LazoUser } from "@/models/LazoUser";
import LazoProductService from "@/services/LazoProductService";
import { BookkeepingFlowCreationRequestV2 } from "@/types/BookkeepingFlowCreationRequestV2";
import { ProductManagerServicesFilters } from "@/types/ProductManagerServicesFilters";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";

export const PRODUCT_FLOW_QUERY_KEY = "product-flow";
export const PRODUCT_SERVICE_MAP_QUERY_KEY = "product-service-map";
export const NO_WORKFLOW_QUERY_KEY = "no-workflow";

type OrderDirection = "ASC" | "DESC" | "asc" | "desc";
type OrderBy = { orderBy: string; direction: OrderDirection };

// ========== QUERIES ==========

export function useProductServiceMap() {
  return useQuery({
    queryKey: [PRODUCT_SERVICE_MAP_QUERY_KEY],
    queryFn: () => LazoProductService.getProductServiceMap().then((r) => r.getContentOrThrowError()),
    staleTime: Infinity,
  });
}

export function useNoWorkflowServices() {
  return useQuery({
    queryKey: [NO_WORKFLOW_QUERY_KEY],
    queryFn: () => LazoProductService.getNoWorkflowServices().then((r) => r.getContentOrThrowError()),
    staleTime: Infinity,
  });
}

export function useFilteredServices(
  lazoEmployees: LazoUser[],
  filters: ProductManagerServicesFilters,
  order: OrderBy,
  page: number,
  pageSize: number,
  showAll: boolean,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: [PRODUCT_FLOW_QUERY_KEY, filters, order, page, pageSize, showAll],
    queryFn: () => LazoProductService.filterProductFlows(filters, order, page, pageSize, showAll).then((r) => r.getContentOrThrowError()),
    enabled: enabled && lazoEmployees?.length > 0,
    placeholderData: keepPreviousData,
  });
}

// ========== MUTATIONS ==========

export function useCreateProductFlow(isFirstWorkflow: boolean) {
  return useMutation({
    mutationFn: (data: BookkeepingFlowCreationRequestV2) =>
      LazoProductService.createBookkeepingFlowV2(data, isFirstWorkflow).then((r) => r.getContentOrThrowError()),
    mutationKey: [PRODUCT_FLOW_QUERY_KEY],
    meta: {
      successMessage: isFirstWorkflow ? "First workflow created successfully" : "New workflow created successfully",
      errorMessage: "Error creating new workflow",
    },
  });
}

interface UpdateDefaultAssignedUserParams {
  lazoProductExternalId: string;
  employeeType: ProductFlowEmployeeType;
  userExternalId: string;
}

export function useUpdateDefaultAssignedUser() {
  return useMutation<string, unknown, UpdateDefaultAssignedUserParams>({
    mutationFn: ({ lazoProductExternalId, employeeType, userExternalId }) =>
      LazoProductService.updateDefaultAssignedUser(lazoProductExternalId, employeeType, userExternalId).then((r) =>
        r.getContentOrThrowError()
      ),
    mutationKey: [PRODUCT_FLOW_QUERY_KEY],
    meta: {
      errorMessage: "Error updating default assigned user",
    },
  });
}

interface UpdateRequiresWorkflowParams {
  lazoProductExternalId: string;
  requiresWorkflow: boolean;
}

export function useUpdateRequiresWorkflow() {
  return useMutation<boolean, unknown, UpdateRequiresWorkflowParams>({
    mutationFn: ({ lazoProductExternalId, requiresWorkflow }) =>
      LazoProductService.updateRequiresWorkflow(lazoProductExternalId, requiresWorkflow).then((r) => r.getContentOrThrowError()),
    mutationKey: [PRODUCT_FLOW_QUERY_KEY],
    meta: {
      errorMessage: "Error updating requires workflow",
    },
  });
}
